<template>
    <v-row dense class="justify-center">

      <!-- Startdatum -->
      <v-col cols="auto">
        <v-text-field
          class="txtStartDate"
          v-model="dateRangeFormatted.start"
          append-inner-icon="mdi-calendar"
          @click:append-inner="openDialogStart"
          density="compact"
          label="Beginn:"
          variant="solo"
          hide-details
          readonly
        />
      </v-col>
      
      <!-- Enddatum -->
      <v-col cols="auto">
        <v-text-field
          class="txtEndDate"
          v-model="dateRangeFormatted.end"
          append-inner-icon="mdi-calendar"
          @click:append-inner="openDialogEnd"
          density="compact"
          label="Ende:"
          variant="solo"
          hide-details
          readonly
        />
      </v-col>
    </v-row>

  <!-- Dialog für Startdatum -->
  <v-dialog 
    v-model="dialogStart" 
    max-width="400"
    :fullscreen="$vuetify.display.smAndDown"
    :scroll-strategy="'none'">
    <v-card>
      <v-card-text>
        <DatePicker 
          v-model="dateRangePicker.start"
          :max="maxEndDate"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn 
          variant="elevated" 
          color="primary" 
          @click="confirmDateStart">
          OK
        </v-btn>
        <v-btn 
          variant="elevated" 
          color="grey" 
          @click="dialogStart = false">
          Abbrechen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Dialog für Enddatum -->
  <v-dialog 
    v-model="dialogEnd" 
    max-width="400"
    :fullscreen="$vuetify.display.smAndDown"
    :scroll-strategy="'none'"
    >
    <v-card>
      <v-card-text>
        <DatePicker 
          v-model="dateRangePicker.end"
          :max="maxEndDate"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn 
          variant="elevated" 
          color="primary" 
          @click="confirmDateEnd">
          OK
        </v-btn>
        <v-btn 
          variant="elevated" 
          color="grey" 
          @click="dialogEnd = false">
          Abbrechen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DatePicker from '../consumables/DatePicker.vue';

export default {
  components: { 
    DatePicker, 
  },

  props: {
    modelValue: {type: Object, default: () => ({ start: '', end: '' })},
  },

  emits: ['update:modelValue'],

  data() {
    return {
      dialogStart: false,
      dialogEnd: false,
      dateRange: {start: '',end: ''},
      dateRangePicker: {start: '',end: ''},
      dateRangeFormatted: {start: '',end: ''},
      maxEndDate: ''
    }
  },

  mounted() {
    this.setDatesFromProp(this.modelValue)

    const tomorrow = this.getTomorrow()
    this.maxEndDate = this.addMonths(tomorrow, 3)

    this.emitUpdate()
  },

  watch: {
    modelValue: {
      handler(newVal) {
        this.setDatesFromProp(newVal)
      },
      deep: true
    }
  },

  methods: {

    setDatesFromProp(val) {
      // Sicherstellen, dass start & end da sind
      this.dateRange.start = val.start || this.getTomorrow()
      this.dateRange.end = val.end || this.getTomorrow()

      this.dateRangePicker.start = this.formatToDDMMYYYY(this.dateRange.start)
      this.dateRangePicker.end = this.formatToDDMMYYYY(this.dateRange.end)

      this.dateRangeFormatted.start = this.dateRangePicker.start
      this.dateRangeFormatted.end = this.dateRangePicker.end

    },

    addMonths(dateStr, months) {
      const d = new Date(dateStr)
      d.setMonth(d.getMonth() + months)

      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },

    openDialogStart() {
      this.dateRangePicker.start = this.dateRangeFormatted.start
      this.dialogStart = true
    },

    openDialogEnd() {
      this.dateRangePicker.end = this.dateRangeFormatted.end
      this.dialogEnd = true
    },

    confirmDateStart() {
      if (!this.dateRangePicker.start) return;
      this.dateRange.start = this.parseDDMMYYYY(this.dateRangePicker.start)

      // Falls Start > End, End anpassen
      if (this.dateRange.start > this.dateRange.end) {
        this.dateRange.end = this.dateRange.start
        this.dateRangeFormatted.end = this.dateRangePicker.start
      }

      this.dateRangeFormatted.start = this.dateRangePicker.start
      this.emitUpdate()
      this.dialogStart = false
    },

    confirmDateEnd() {
      if (!this.dateRangePicker.end) return;
      this.dateRange.end = this.parseDDMMYYYY(this.dateRangePicker.end)

      // Falls End < Start, Start anpassen
      if (this.dateRange.end < this.dateRange.start) {
        this.dateRange.start = this.dateRange.end
        this.dateRangeFormatted.start = this.dateRangePicker.end
      }

      this.dateRangeFormatted.end = this.dateRangePicker.end
      this.emitUpdate()
      this.dialogEnd = false
    },

    emitUpdate() {
      this.$emit('update:modelValue', { 
        start: this.dateRange.start, 
        end: this.dateRange.end 
      })
    },

    getTomorrow() {
      const today = new Date()
      const yyyy = today.getFullYear()
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      const dd = String(today.getDate() + 1 ).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },

    formatToDDMMYYYY(dateStr) {
      if (!dateStr) return ''
      const [yyyy, mm, dd] = dateStr.split('-')
      return `${dd}.${mm}.${yyyy}`
    },

    parseDDMMYYYY(formatted) {
      const parts = formatted.split('.')
      if (parts.length !== 3) return ''
      const [dd, mm, yyyy] = parts
      return `${yyyy}-${mm}-${dd}`
    },
  }
}
</script>

<style scoped>
.txtStartDate, 
.txtEndDate {
  border-radius: 4px;
  box-shadow: 0px 6px 15px hsla(0, 0%, 0%, 0.5);
  width: auto !important;
  min-width: 9rem !important;
}
</style>